import { PopoverHeading, StatusPopover, cx, formatDateTime } from "@amber/ui";
import type { PriorityView } from "../../contracts/index";

export const LEVEL_TEXT = { high: "High", medium: "Medium", low: "Low" } as const;

function barsOf(level: PriorityView["level"]): number {
  if (level === "high") return 3;
  return level === "medium" ? 2 : 1;
}

function PriorityBars({ level }: { level: PriorityView["level"] }) {
  const filled = barsOf(level);
  return (
    <span aria-hidden="true" className="inline-flex h-3 items-end gap-[2px]">
      {[1, 2, 3].map((n) => (
        <span
          key={n}
          className={cx("w-[3px] rounded-[1px]", n <= filled ? "bg-fg" : "bg-border")}
          style={{ height: `${String(n * 4)}px` }}
        />
      ))}
    </span>
  );
}

export function PriorityCell({
  priority,
  blocks,
  openedAt,
}: {
  priority: PriorityView;
  blocks?: string;
  openedAt: string;
}) {
  const label = LEVEL_TEXT[priority.level];
  return (
    <StatusPopover
      label={
        <span
          className={cx(
            "inline-flex items-center gap-1.5 underline decoration-dotted underline-offset-2",
            priority.level === "high" ? "font-semibold text-fg" : "text-fg",
          )}
        >
          <PriorityBars level={priority.level} />
          {label}
        </span>
      }
      accessibleLabel={`${label} priority, details`}
    >
      <PopoverHeading>
        {label} priority · ranked {priority.rank} of {priority.of}
      </PopoverHeading>
      <p>
        {priority.type_label} {priority.type_weight} ·{" "}
        {priority.age_days === 1 ? "1 day" : `${String(priority.age_days)} days`} old +{priority.age} ·{" "}
        {priority.people === 1 ? "1 person" : `${String(priority.people)} people`} +{priority.impact_people}
        {priority.impact_items > 0 ? ` · ${String(priority.items)} items +${String(priority.impact_items)}` : ""} ={" "}
        <b>{priority.score}</b>
      </p>
      <dl>
        {blocks ? (
          <>
            <dt>Blocks</dt>
            <dd>{blocks}</dd>
          </>
        ) : null}
        <dt>Waiting since</dt>
        <dd>{formatDateTime(openedAt)}</dd>
      </dl>
      <p className="mt-2 text-md text-muted">Computed automatically. Nobody assigns tasks.</p>
    </StatusPopover>
  );
}
